import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	Tab,
	Tabs,
	TextField,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { percent, px } from 'csx'
import { useFormik } from 'formik'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import React, { SyntheticEvent, useEffect, useMemo } from 'react'
import * as yup from 'yup'
import { mediaWidths } from '../../../styles'
import { FixedHeaderWithHint } from '../../../utils/HOC/FixedHeaders'
import { DrawSignature } from './DrawSignature'
import { Signature } from './SignatureContext'
import {
	SignatureStoreProvider,
	useSignatureStore,
} from './SignatureModalStore'
import { SignatureView } from './SignatureView'

type SignatureProps = {
	onCancel: () => void
	onApply: (signature: Signature) => Promise<void>
}

export const SignatureWrapper: React.FC<SignatureProps> = (
	props: SignatureProps,
) => {
	return (
		<SignatureStoreProvider>
			<SignatureModal {...props} />
		</SignatureStoreProvider>
	)
}

// TabPanel component from Material UI https://codesandbox.io/s/kz25m?file=/demo.tsx:1170-1175

type TabPanelProps = {
	children?: React.ReactNode
	index: unknown
	value: unknown
}

const TabPanel: React.FC<TabPanelProps> = ({
	children,
	index,
	value,
}: TabPanelProps) => {
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
		>
			{value === index && (
				<Box
					sx={{
						p: 3,
					}}
				>
					{children}
				</Box>
			)}
		</div>
	)
}

export const SignatureModal: React.FC<SignatureProps> = observer(
	(props: SignatureProps) => {
		const styles = useStyles()

		const modalStore = useSignatureStore()

		const localStore = useLocalObservable(() => ({
			selectedTabIndex: 0,
			emptySignature:
				modalStore.signatureData.viewSignatureValue === undefined &&
				modalStore.signatureData.drawSignatureValue === undefined,
			isApplying: false,
		}))

		const validationSchema = useMemo(
			() =>
				yup.object({
					fullName: yup
						.string()
						.required('Full name is required')
						.test('is-valid', 'Please enter a valid name', (value) => {
							// allows extended latin characters as well as "-", "." and "'"
							// with a space inbetween, but no trailing space
							// does *not* allow for any non-latin characters ex: korean, chinese, russian
							// but does allow accents ex: María José Carreño Quiñones
							const nameRegex =
								/^[\u00c0-\u01ffa-zA-Z'-.]{2,}(?: [\u00c0-\u01ffa-zA-Z'-.]+){0,}$/

							return value !== undefined && nameRegex.test(value)
						}),
					initials: yup
						.string()
						.required('Initials are required')
						.test(
							'is-valid',
							'Please enter valid and capitalized initials',
							(value) => {
								return value !== undefined && /^[^a-z]{2,}$/.test(value)
							},
						),
					email: yup
						.string()
						.required('Email is required')
						.email('Please enter a valid email address'),
				}),
			[],
		)

		const formik = useFormik({
			initialValues: {
				fullName: modalStore.fullName,
				initials: modalStore.initials,
				email: modalStore.email,
			},
			validationSchema,
			onSubmit: (values) => {
				runInAction(() => {
					localStore.isApplying = true
				})
				props
					.onApply({
						fullName: values.fullName,
						initials: values.initials,
						email: values.email,
						signatureData:
							localStore.selectedTabIndex == 0
								? modalStore.signatureData.viewSignatureValue ?? ''
								: modalStore.signatureData.drawSignatureValue ?? '',
						id: 0,
					})
					.finally(
						action(() => {
							localStore.isApplying = false
						}),
					)
			},
		})

		useEffect(
			action(() => {
				if (localStore.selectedTabIndex == 0)
					localStore.emptySignature =
						modalStore.signatureData.viewSignatureValue === undefined
				else
					localStore.emptySignature =
						modalStore.signatureData.drawSignatureValue === undefined
			}),
			[
				localStore.selectedTabIndex,
				modalStore.signatureData.viewSignatureValue,
				modalStore.signatureData.drawSignatureValue,
			],
		)

		return (
			<Dialog open={true} fullWidth>
				<DialogTitle>Create New Signature</DialogTitle>
				<DialogContent>
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							marginTop: px(20),
						}}
					>
						<FixedHeaderWithHint
							label="Full Name"
							className={styles.fullNameText}
							hint={formik.touched.fullName && formik.errors.fullName}
							hintColor="error.main"
						>
							<TextField
								fullWidth
								id="fullName"
								name="fullName"
								value={formik.values.fullName}
								error={!!formik.errors.fullName && formik.touched.fullName}
								onChange={(evt) => {
									formik.handleChange(evt)
									modalStore.fullName = evt.target.value
								}}
								onBlur={formik.handleBlur}
								disabled={formik.isSubmitting}
								autoComplete="off"
							/>
						</FixedHeaderWithHint>
						<FixedHeaderWithHint
							label="Initials"
							className={styles.initialsText}
							hint={formik.touched.initials && formik.errors.initials}
							hintColor="error.main"
						>
							<TextField
								fullWidth
								id="initials"
								name="initials"
								value={formik.values.initials}
								error={!!formik.errors.initials && formik.touched.initials}
								onChange={(evt) => {
									formik.handleChange(evt)
									modalStore.initials = evt.target.value
								}}
								onBlur={formik.handleBlur}
								disabled={formik.isSubmitting}
								autoComplete="off"
							/>
						</FixedHeaderWithHint>
					</div>
					<div>
						<FixedHeaderWithHint
							label="Email Address"
							className={styles.emailText}
							hint={formik.touched.email && formik.errors.email}
							hintColor="error.main"
						>
							<TextField
								fullWidth
								id="email"
								name="email"
								value={formik.values.email}
								error={!!formik.errors.email && formik.touched.email}
								onChange={(evt) => {
									formik.handleChange(evt)
									modalStore.email = evt.target.value
								}}
								onBlur={formik.handleBlur}
								disabled={formik.isSubmitting}
								autoComplete="off"
							/>
						</FixedHeaderWithHint>
					</div>
					<Tabs
						indicatorColor="secondary"
						textColor="primary"
						value={localStore.selectedTabIndex}
						onChange={action((event: SyntheticEvent, v: number) => {
							localStore.selectedTabIndex = v
						})}
					>
						<Tab label="View" />
						<Tab label="Draw" />
					</Tabs>
					<TabPanel value={localStore.selectedTabIndex} index={0}>
						<SignatureView />
					</TabPanel>
					<TabPanel value={localStore.selectedTabIndex} index={1}>
						<DrawSignature />
					</TabPanel>
					<DialogActions>
						<Button
							variant="outlined"
							onClick={props.onCancel}
							disabled={localStore.isApplying}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={formik.submitForm}
							disabled={!formik.isValid || formik.isSubmitting}
						>
							Apply
						</Button>
					</DialogActions>
				</DialogContent>

				{localStore.isApplying && <LinearProgress />}
			</Dialog>
		)
	},
)

const useStyles = makeStyles({
	root: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexFlow: 'column',
	},
	modal: {
		width: percent(100),
	},
	card: {
		alignItems: 'center',
		justifyContent: 'center',
		display: 'flex',
		flexFlow: 'row wrap',
		[`@media (max-width: ${mediaWidths.medium.maxWidth}px)`]: {
			maxWidth: px(300),
			minWidth: px(300),
		},
		[`@media (min-width: ${mediaWidths.medium.minWidth}px)`]: {
			maxWidth: px(850),
			minWidth: px(850),
		},
		minHeight: px(500),
		paddingTop: px(10),
		paddingBottom: px(10),
	},
	saveModal: {
		alignItems: 'center',
		justifyContent: 'center',
		display: 'flex',
		flexFlow: 'row wrap',
		[`@media (max-width: ${mediaWidths.medium.maxWidth}px)`]: {
			maxWidth: px(200),
			minWidth: px(200),
		},
		[`@media (min-width: ${mediaWidths.medium.minWidth}px)`]: {
			maxWidth: px(400),
			minWidth: px(400),
		},
		minHeight: px(200),
		paddingTop: px(10),
		paddingBottom: px(10),
	},
	fullNameText: {
		marginRight: px(5),
		width: percent(100),
	},
	initialsText: {
		marginLeft: px(5),
		width: percent(50),
	},
	emailText: {
		width: percent(66),
	},
})
